import type {
  InstallmentConfigurationData,
  LOCConfigurationData,
} from 'core/types/generated/loan-types/types'

export const defaultInstallmentLoanType: InstallmentConfigurationData = {
  type: 'installment',
  name: 'New Installment Loan Type',
  assetType: 'personalClosedEndUnsecured',
  assetDisplayName: 'Asset Display Name',
  automatedDebtValidationNotification: [
    ['IL', 'US', 'debtValidationNoticeAutomatic'],
  ],
  autoPay: {
    achDebitTiming: 'initiateOnDueDate',
    achDebitTimingShiftOnNonBusinessDay: 'forward',
    allowedFrequency: {
      isDueDatesAvailableEveryTwoWeeks: false,
      isDueDatesAvailableMonthly: true,
      isDueDatesAvailableTwiceMonthly: true,
      isDueDatesAvailableWeekly: false,
      monthlyOptions: [1, 5, 10, 15, 20],
      twiceMonthlyOptions: [
        [1, 15],
        [15, 31],
      ],
    },
    amountChangeNotificationBuffer: 10,
    amountChangePercentageThreshold: 0.1,
    numberOfConsecutiveFailedAttempts: 0,
    paymentsRetry: {
      doNotRetryAfterMaxDays: 0,
      isRetryAutopayFailedPayments: false,
      maxNumberOfAutoRetriesPerPayment: 0,
      retryBufferDayType: 'business',
      retryBufferInDays: 0,
      retryDayMethod: 'fixedBuffer',
      retryDayOfWeek: 0,
      retryDayOfWeekMinBuffer: 0,
    },
  },
  creditReporting: {
    isCreditReportingAllowed: true,
  },
  dueDates: {
    allowedFrequencyChanges: {
      everyTwoWeeksToEveryTwoWeeksDay: true,
      everyTwoWeeksToMonthly: true,
      everyTwoWeeksToTwiceMonthly: true,
      everyTwoWeeksToWeekly: true,
      monthlyToEveryTwoWeeks: true,
      monthlyToMonthlyDay: true,
      monthlyToTwiceMonthly: true,
      monthlyToWeekly: true,
      twiceMonthlyToEveryTwoWeeks: true,
      twiceMonthlyToMonthly: true,
      twiceMonthlyToTwiceMonthlyDay: true,
      twiceMonthlyToWeekly: true,
      weeklyToEveryTwoWeeks: true,
      weeklyToMonthly: true,
      weeklyToTwiceMonthly: true,
      weeklyToWeeklyDay: true,
    },
    allowedFrequencyNewLoan: {
      isDueDatesAvailableEveryTwoWeeks: true,
      isDueDatesAvailableMonthly: true,
      isDueDatesAvailableSingleTerm: false,
      isDueDatesAvailableTwiceMonthly: true,
      isDueDatesAvailableWeekly: true,
    },
    dateAdjustment: 'backward',
    dayType: 'business',
    gapBetweenDueDates: {
      everyTwoWeeks: { min: 7, max: 21 },
      monthly: { min: 10, max: 46 },
      twiceMonthly: { min: 7, max: 23 },
      weekly: { min: 4, max: 10 },
    },
    isShowRevisedTermsOnFrequencyChange: true,
    periodType: 'periodEnd',
    singleTermUnitPeriod: 'days',
  },
  duePayments: {
    allowPrepayments: false,
    amortizationMethod: 'equalPayments',
  },
  extraFields: {},
  fees: {
    originationFeeAPRCalcOverride: 'atOrigination',
    originationFeeChargeLogic: 'onTopPeriodicPayment',
  },
  futurePay: {
    achDebitTiming: 'initiateOnDueDate',
    achDebitTimingShiftOnNonBusinessDay: 'forward',
  },
  interest: {
    accrualMethod: 'simple',
    annualInterestRateRange: [
      { country: 'US', state: 'TX', minRate: 0, maxRate: 0.1 },
    ],
    aprMethod: 'usRule',
    aprRange: [{ country: 'US', state: 'TX', minRate: 0, maxRate: 0.1 }],
    dailyInterestCalculation: 365,
    isInterestAmountCap: false,
    periodAccrualDurationType: 'actual',
  },
  managedByPeach: true,
  paydayNotices: {
    sendConsumerRightsNotice: false,
    sendFirstPaymentWithdrawalNotice: false,
  },
  paymentAllowedWhenChargedOff: true,
  refunds: {
    isRefundAllowed: true,
  },
  snapshots: {
    reportDaysWhenPaidOff: 10,
  },
  statements: {
    installmentStatementCreationDaysOffsets: {
      everyTwoWeeks: 0,
      monthly: 0,
      twiceMonthly: 0,
      weekly: 0,
    },
    isCreateAndSendPDF: false,
    lateFeeUpToAmount: 100,
    statementsEnabled: true,
  },
  terms: {
    accelerationDueWithin: 30,
    chargeOffDueWithin: 30,
    durationExtensionMaxDays: 15,
    rightToCureBuffer: 30,
  },
  useExternalIdInUI: false,
}

export type { InstallmentConfigurationData }

export const defaultLineOfCreditLoanType: LOCConfigurationData = {
  type: 'lineOfCredit',
  lineOfCreditType: 'revolving',
  name: 'New Line of Credit Loan Type',
  assetType: 'personalClosedEndUnsecured',
  assetDisplayName: 'Asset Display Name',
  managedByPeach: true,
  paymentAllowedWhenChargedOff: true,
  creditReporting: { isCreditReportingAllowed: true },
  autopay: {
    achDebitTiming: 'initiateOnDueDate',
    achDebitTimingShiftOnNonBusinessDay: 'forward',
    calculationLogic: 'dynamic',
    amountChangeNotificationBuffer: 14,
    amountChangePercentageThreshold: 0.3,
    numberOfConsecutiveFailedAttempts: 0,
    paymentsRetry: {
      isRetryAutopayFailedPayments: true,
      maxNumberOfAutoRetriesPerPayment: 0,
      retryDayMethod: 'fixedBuffer',
      retryBufferDayType: 'business',
      retryBufferInDays: 0,
      doNotRetryAfterMaxDays: 0,
      retryDayOfWeek: 0,
      retryDayOfWeekMinBuffer: 0,
    },
  },
  futurePay: {
    achDebitTiming: 'initiateOnDueDate',
    achDebitTimingShiftOnNonBusinessDay: 'forward',
  },
  fees: {
    originationFeeChargeLogic: 'onTopPeriodicPayment',
    originationFeeAPRCalcOverride: 'noOverride',
  },
  interest: {
    accrualMethod: 'simple',
    dailyInterestCalculation: 365,
    periodAccrualDurationType: 'actual',
    aprMethod: 'usRule',
    isInterestAmountCap: false,
    annualInterestRateRange: [],
    aprRange: [],
  },
  duePayments: {
    amortizationMethod: 'equalPayments',
  },
  terms: {
    accelerationDueWithin: 120,
    chargeOffDueWithin: 120,
  },
  statements: {
    statementsEnabled: true,
    lateFeeUpToAmount: 100,
    isCreateAndSendPDF: true,
    locStatementCreationDaysOffset: {
      weekly: 3,
      everyTwoWeeks: 14,
      twiceMonthly: 14,
      monthly: 21,
    },
  },
  useExternalIdInUI: false,
}
